import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ExclamationCircleOutlined,
  OrderedListOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { App, DatePicker, Pagination, Segmented } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { DecomposeDialog } from '../components/nhiem-vu/DecomposeDialog';
import { IconAi } from '../components/nhiem-vu/IconAi';
import { TaskDetailDrawer } from '../components/nhiem-vu/TaskDetailDrawer';
import type { FormMode } from '../components/nhiem-vu/TaskFormDialog';
import { TaskFormDialog } from '../components/nhiem-vu/TaskFormDialog';
import { TaskKpiStrip } from '../components/nhiem-vu/TaskKpiStrip';
import { TaskNotificationBell } from '../components/nhiem-vu/TaskNotificationBell';
import { TaskStatsPanel } from '../components/nhiem-vu/TaskStatsPanel';
import { TaskTreeList } from '../components/nhiem-vu/TaskTreeList';
import { NHAN_PHAM_VI, NHAN_TRANG_THAI, NHAN_VAI_TRO } from '../constants';
import {
  useCayNhiemVu,
  useDoiTrangThai,
  useDonViDanhBa,
  useGiaoNhiemVu,
  useNutNhiemVu,
  useSuaNhiemVu,
  useTaiTepNhiemVu,
  useThongKeNhiemVu,
  useXoaNhiemVu,
} from '../hooks/useNhiemVu';
import { useQuyenVanBan } from '../hooks/useQuyenVanBan';
import {
  CO_TRANG,
  DINH_DANG_API,
  DINH_DANG_NGAY,
  demHauDue,
  LOC_TRANG_THAI,
  PHAM_VI,
  timNut,
  VAI_TRO_THAM_GIA,
  vaiTroLoc,
} from '../nghiep-vu/nhiem-vu';
import type { GiaoNhiemVuVao, LocTrangThai, NutNhiemVu, PhamViNhiemVu, VaiTroThamGia } from '../types-nhiem-vu';

const TRANG_PHAN_CONG = '/xu-ly-van-ban/phan-cong';
const THAM_SO_VIEC = 'task';
const TRE_TIM_MS = 300;
const SO_DONG_CHO = 4;
const LS_PHAM_VI = 'nhiemVu.scope';
const LS_THONG_KE = 'nhiemVu.statsOpen';

type Khoang = [Dayjs | null, Dayjs | null] | null;

function docLuu<T extends string>(khoa: string, choPhep: readonly T[], macDinh: T): T {
  try {
    const v = localStorage.getItem(khoa);
    return choPhep.includes(v as T) ? (v as T) : macDinh;
  } catch {
    // Trình duyệt chặn storage (chế độ riêng tư) — dùng mặc định.
    return macDinh;
  }
}

function ghiLuu(khoa: string, giaTri: string) {
  try {
    localStorage.setItem(khoa, giaTri);
  } catch {
    // Chỉ là tiện ích nhớ lựa chọn, mất thì thôi.
  }
}

function useTre(giaTri: string): string {
  const [tre, setTre] = useState(giaTri);
  useEffect(() => {
    const hen = setTimeout(() => setTre(giaTri.trim()), TRE_TIM_MS);
    return () => clearTimeout(hen);
  }, [giaTri]);
  return tre;
}

/** Quản lý nhiệm vụ (`/xu-ly-van-ban/quan-ly-nhiem-vu`), chuyển từ foxainative. */
export function QuanLyNhiemVuView() {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const { modal } = App.useApp();
  const dieuHuong = useDieuHuong();
  const quyen = useQuyenVanBan('task');

  const [phamVi, setPhamVi] = useState<PhamViNhiemVu>(() => docLuu(LS_PHAM_VI, PHAM_VI, 'mine'));
  const [moThongKe, setMoThongKe] = useState(() => docLuu(LS_THONG_KE, ['1', '0'] as const, '0') === '1');
  const [vaiTroCon, setVaiTroCon] = useState<VaiTroThamGia | ''>('');
  const [khoang, setKhoang] = useState<Khoang>(() => [dayjs().startOf('year'), dayjs()]);
  const [oTim, setOTim] = useState('');
  const tim = useTre(oTim);
  const [trangThai, setTrangThai] = useState<LocTrangThai | ''>('');
  const [donVi, setDonVi] = useState('');
  const [trang, setTrang] = useState(1);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [soForm, setSoForm] = useState(0);
  const [phanRaCha, setPhanRaCha] = useState<NutNhiemVu | null>(null);

  // Ngăn chi tiết gắn với URL (?task=) để thông báo và đường link chia sẻ mở thẳng đúng việc.
  const [thamSo, setThamSo] = useSearchParams();
  const idMo = thamSo.get(THAM_SO_VIEC);
  const datIdMo = (id: string | null) =>
    setThamSo(
      (cu) => {
        const moi = new URLSearchParams(cu);
        if (id) moi.set(THAM_SO_VIEC, id);
        else moi.delete(THAM_SO_VIEC);
        return moi;
      },
      { replace: true },
    );

  // Đổi bộ lọc thì về trang 1 (đổi trực tiếp ở chỗ đặt lọc, không qua effect).
  const loc = <T,>(dat: (v: T) => void) => (v: T) => {
    dat(v);
    setTrang(1);
  };
  const doiTim = (v: string) => {
    setOTim(v);
    setTrang(1);
  };

  const truyVanChung = {
    date_from: khoang?.[0]?.format(DINH_DANG_API),
    date_to: khoang?.[1]?.format(DINH_DANG_API),
    role: vaiTroLoc(phamVi, vaiTroCon),
  };
  const thongKe = useThongKeNhiemVu(truyVanChung);
  const cay = useCayNhiemVu({
    ...truyVanChung,
    page: trang,
    page_size: CO_TRANG,
    search: tim || undefined,
    task_status: trangThai || undefined,
    lead_org_id: donVi || undefined,
  });
  const dsDonVi = useDonViDanhBa();

  const giao = useGiaoNhiemVu();
  const sua = useSuaNhiemVu();
  const doiTrangThai = useDoiTrangThai();
  const xoa = useXoaNhiemVu();
  const taiTep = useTaiTepNhiemVu();

  const tasks = useMemo(() => cay.data?.data ?? [], [cay.data]);
  const thay = idMo ? timNut(tasks, idMo) : null;
  // Việc mở từ thông báo có thể không nằm trong trang/bộ lọc hiện tại — tải riêng nó.
  const nutRieng = useNutNhiemVu(idMo, idMo !== null && !cay.isLoading && thay === null);
  const dangMo = thay ?? (nutRieng.data ? { task: nutRieng.data, parent: null } : null);
  const orgs = dsDonVi.data ?? [];

  const doiPhamVi = (moi: PhamViNhiemVu) => {
    setPhamVi(moi);
    setVaiTroCon('');
    setTrang(1);
    ghiLuu(LS_PHAM_VI, moi);
  };
  const batTatThongKe = () => {
    const moi = !moThongKe;
    setMoThongKe(moi);
    ghiLuu(LS_THONG_KE, moi ? '1' : '0');
  };

  const coLoc = Boolean(tim || trangThai || donVi || vaiTroCon);
  const xoaLoc = () => {
    setOTim('');
    setTrangThai('');
    setDonVi('');
    setVaiTroCon('');
    setTrang(1);
  };

  const moForm = (m: FormMode) => {
    setSoForm((n) => n + 1);
    setFormMode(m);
  };

  const hoiXoa = (nv: NutNhiemVu) => {
    const so = demHauDue(nv);
    modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: t('vanBanNhiemVu.xoa.tieuDe', { ten: nv.title }),
      content: so > 0 ? t('vanBanNhiemVu.xoa.thanKemCon', { so }) : t('vanBanNhiemVu.xoa.than'),
      okText: t('vanBanNhiemVu.xoa.nut'),
      okButtonProps: { danger: true },
      cancelText: t('chung.huy'),
      onOk: async () => {
        await xoa.mutateAsync(nv.id);
        if (idMo === nv.id) datIdMo(null);
      },
    });
  };

  const capNhat = (id: string, input: GiaoNhiemVuVao) => sua.mutateAsync({ id, truong: input, nguoi: input.participants });

  const moc: { label: string; value: [Dayjs, Dayjs] }[] = [
    { label: t('vanBanNhiemVu.khoang.thangNay'), value: [dayjs().startOf('month'), dayjs()] },
    { label: t('vanBanNhiemVu.khoang.quyNay'), value: [dayjs().month(dayjs().month() - (dayjs().month() % 3)).startOf('month'), dayjs()] },
    { label: t('vanBanNhiemVu.khoang.namNay'), value: [dayjs().startOf('year'), dayjs()] },
  ];

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        breadcrumb={[{ label: t('vanBanNhiemVu.breadcrumbNhom') }, { label: t('vanBanNhiemVu.tieuDe') }]}
        title={t('vanBanNhiemVu.tieuDe')}
        subtitle={t('vanBanNhiemVu.moTa')}
        actions={
          <>
            <TaskNotificationBell onOpenTask={(item) => datIdMo(item.task_id)} />
            <Button variant="outline" icon={<IconAi />} onClick={() => dieuHuong(TRANG_PHAN_CONG)}>
              {t('vanBanNhiemVu.phanCongAi')}
            </Button>
            {quyen.canCreate && (
              <Button variant="primary" icon={<PlusOutlined />} onClick={() => moForm({ kind: 'create' })}>
                {t('vanBanNhiemVu.giaoMoi')}
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <Segmented<PhamViNhiemVu>
          aria-label={t('vanBanNhiemVu.phamVi.nhan')}
          value={phamVi}
          onChange={doiPhamVi}
          options={PHAM_VI.map((s) => ({ value: s, label: t(NHAN_PHAM_VI[s]) }))}
        />
        {phamVi === 'mine' && (
          <Select<VaiTroThamGia | ''>
            aria-label={t('vanBanNhiemVu.phamVi.moiVaiTro')}
            className="w-48"
            value={vaiTroCon}
            onChange={loc(setVaiTroCon)}
            options={[
              { value: '', label: t('vanBanNhiemVu.phamVi.moiVaiTro') },
              ...VAI_TRO_THAM_GIA.map((r) => ({ value: r, label: t(NHAN_VAI_TRO[r]) })),
            ]}
          />
        )}
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          <span className="text-xs text-content-muted">{t('vanBanNhiemVu.khoang.nhan')}</span>
          <DatePicker.RangePicker
            aria-label={t('vanBanNhiemVu.khoang.nhan')}
            format={DINH_DANG_NGAY}
            allowEmpty={[true, true]}
            placeholder={[t('vanBanNhiemVu.khoang.tuNgay'), t('vanBanNhiemVu.khoang.denNgay')]}
            presets={moc}
            value={khoang}
            onChange={(v) => loc(setKhoang)(v)}
          />
        </div>
      </div>

      <TaskKpiStrip
        dashboard={thongKe.data}
        loading={thongKe.isLoading}
        error={thongKe.isError}
        onRetry={() => void thongKe.refetch()}
        active={trangThai}
        onPick={loc(setTrangThai)}
      />

      <TaskStatsPanel dashboard={thongKe.data} open={moThongKe} onToggle={batTatThongKe} />

      <section aria-labelledby="nv-ds-tieu-de" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <OrderedListOutlined aria-hidden className="text-primary-600" />
          <h2 id="nv-ds-tieu-de" className="text-sm font-semibold text-content-primary">
            {t('vanBanNhiemVu.ds.tieuDe')}
          </h2>
          {cay.data && !cay.isError && (
            <span className="rounded bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              {t('vanBanNhiemVu.ds.soChinh', { so: soVN(cay.data.total, locale) })}
            </span>
          )}
          <span className="text-xs text-content-muted">{t('vanBanNhiemVu.ds.sapXepMo')}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
          <div className="min-w-56 flex-1">
            <Input
              allowClear
              aria-label={t('vanBanNhiemVu.ds.tim')}
              prefix={<SearchOutlined className="text-content-muted" />}
              value={oTim}
              maxLength={200}
              onChange={(e) => doiTim(e.target.value)}
              placeholder={t('vanBanNhiemVu.ds.tim')}
            />
          </div>
          <Select<LocTrangThai | ''>
            aria-label={t('vanBanNhiemVu.ds.moiTrangThai')}
            className="w-44"
            value={trangThai}
            onChange={loc(setTrangThai)}
            options={[
              { value: '', label: t('vanBanNhiemVu.ds.moiTrangThai') },
              ...LOC_TRANG_THAI.map((s) => ({ value: s, label: t(NHAN_TRANG_THAI[s]) })),
            ]}
          />
          <Select<string>
            aria-label={t('vanBanNhiemVu.ds.moiDonVi')}
            className="w-56"
            showSearch
            optionFilterProp="label"
            value={donVi}
            onChange={loc(setDonVi)}
            loading={dsDonVi.isLoading}
            options={[{ value: '', label: t('vanBanNhiemVu.ds.moiDonVi') }, ...orgs.map((o) => ({ value: o.id, label: o.name }))]}
          />
          {coLoc && (
            <Button variant="ghost" onClick={xoaLoc}>
              {t('chung.xoaBoLoc')}
            </Button>
          )}
        </div>

        <ThanDanhSach
          tai={cay.isLoading}
          loi={cay.isError ? dichLoi(cay.error) : null}
          onThuLai={() => void cay.refetch()}
          rong={tasks.length === 0}
          coLoc={coLoc}
          phamVi={phamVi}
          nutTao={
            quyen.canCreate ? (
              <Button variant="primary" icon={<PlusOutlined />} onClick={() => moForm({ kind: 'create' })}>
                {t('vanBanNhiemVu.giaoMoi')}
              </Button>
            ) : undefined
          }
        >
          <TaskTreeList
            tasks={tasks}
            orgs={orgs}
            quyen={quyen}
            onOpen={(x) => datIdMo(x.id)}
            onAddChild={(cha) => moForm({ kind: 'child', parent: cha })}
            onDecompose={setPhanRaCha}
            onEdit={(x) => moForm({ kind: 'edit', task: x })}
            onDelete={hoiXoa}
            onStatus={(x, s) => doiTrangThai.mutate({ id: x.id, status: s })}
          />
          {(cay.data?.total_pages ?? 1) > 1 && (
            <div className="flex justify-end">
              <Pagination
                current={trang}
                pageSize={CO_TRANG}
                total={cay.data?.total ?? 0}
                showSizeChanger={false}
                onChange={setTrang}
              />
            </div>
          )}
        </ThanDanhSach>
      </section>

      <TaskDetailDrawer
        task={idMo === null ? undefined : (dangMo?.task ?? null)}
        loi={nutRieng.isError ? dichLoi(nutRieng.error) : null}
        parentTitle={dangMo?.parent?.title ?? null}
        orgs={orgs}
        quyen={quyen}
        statusPending={doiTrangThai.isPending}
        onClose={() => datIdMo(null)}
        onOpenTask={(x) => datIdMo(x.id)}
        onAddChild={(cha) => moForm({ kind: 'child', parent: cha })}
        onDecompose={setPhanRaCha}
        onEdit={(x) => moForm({ kind: 'edit', task: x })}
        onDelete={hoiXoa}
        onStatus={(x, s) => doiTrangThai.mutate({ id: x.id, status: s })}
        onDownload={(x) => taiTep.mutate(x)}
      />
      {phanRaCha && (
        <DecomposeDialog key={phanRaCha.id} parent={phanRaCha} orgs={orgs} onClose={() => setPhanRaCha(null)} />
      )}
      {formMode && (
        <TaskFormDialog
          key={soForm}
          mode={formMode}
          orgs={orgs}
          saving={giao.isPending || sua.isPending}
          onClose={() => setFormMode(null)}
          onCreate={(input) => giao.mutateAsync(input)}
          onUpdate={capNhat}
        />
      )}
    </div>
  );
}

function ThanDanhSach({
  tai,
  loi,
  onThuLai,
  rong,
  coLoc,
  phamVi,
  nutTao,
  children,
}: {
  tai: boolean;
  loi: string | null;
  onThuLai: () => void;
  rong: boolean;
  coLoc: boolean;
  phamVi: PhamViNhiemVu;
  nutTao: ReactNode;
  children: ReactNode;
}) {
  const { t } = useT();
  if (loi !== null) {
    return (
      <div className="rounded-lg border border-default bg-surface p-6 text-center shadow-sm">
        <p role="alert" className="text-sm text-danger-700">
          {t('vanBanNhiemVu.ds.loi', { loi })}
        </p>
        <Button className="mt-3" variant="outline" icon={<ReloadOutlined />} onClick={onThuLai}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  if (tai) {
    return (
      <div className="space-y-2 rounded-lg border border-default bg-surface p-4 shadow-sm">
        {Array.from({ length: SO_DONG_CHO }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (rong) {
    const cuaToi = phamVi === 'mine';
    return (
      <div className="rounded-lg border border-default bg-surface shadow-sm">
        {coLoc ? (
          // Rỗng vì bộ lọc: không mời "tạo cái đầu tiên" (CLAUDE.md); nút xoá lọc đã có ở thanh lọc.
          <Empty icon={SearchOutlined} title={t('vanBanNhiemVu.ds.rongLoc')} />
        ) : (
          <Empty
            icon={OrderedListOutlined}
            title={cuaToi ? t('vanBanNhiemVu.ds.rongCuaToiTieuDe') : t('vanBanNhiemVu.ds.rongTieuDe')}
            description={cuaToi ? t('vanBanNhiemVu.ds.rongCuaToiMo') : t('vanBanNhiemVu.ds.rongMo')}
            action={nutTao}
          />
        )}
      </div>
    );
  }
  return <>{children}</>;
}
